<template>
  <BaseDialog
    :title="'系統資訊'"
    :width="'600px'"
    :btn1="'複製'"
    :btn2="'關閉'"
    append-to-body
    @close="onClose"
    @cancel="onClose"
    @confirm="copyAllSystemInfo"
  >
    <div class="system-info" style="margin-bottom: 40px">
      <div class="current-time">
        <h3>{{ systemInfo.currentTime }}</h3>
      </div>

      <div class="info-group">
        <h4>裝置資訊</h4>
        <div class="info-item">
          <span class="label">作業系統：</span>
          <span class="value">{{ systemInfo.os }}</span>
        </div>
        <div class="info-item">
          <span class="label">瀏覽器：</span>
          <span class="value">{{ systemInfo.browser }}</span>
        </div>
        <div class="info-item">
          <span class="label">裝置類型：</span>
          <span class="value">{{ systemInfo.deviceType }}</span>
        </div>
        <div class="info-item">
          <span class="label">螢幕解析度：</span>
          <span class="value">{{ systemInfo.screenResolution }}</span>
        </div>
        <div class="info-item">
          <span class="label">視窗大小：</span>
          <span class="value">{{ systemInfo.windowSize }}</span>
        </div>
        <div class="info-item">
          <span class="label">使用語言：</span>
          <span class="value">{{ systemInfo.language }}</span>
        </div>
        <div class="info-item">
          <span class="label">瀏覽器語言：</span>
          <span class="value">{{ systemInfo.browserLanguages }}</span>
        </div>
        <div class="info-item">
          <span class="label">i18n語言：</span>
          <span class="value">{{ systemInfo.displayLanguage }}</span>
        </div>
        <div class="info-item">
          <span class="label">網路狀態：</span>
          <span class="value">{{ systemInfo.networkStatus }}</span>
        </div>
        <div class="info-item">
          <span class="label">時區：</span>
          <span class="value">{{ systemInfo.timezone }}</span>
        </div>
        <div class="info-item">
          <span class="label">User Agent：</span>
          <span class="value user-agent">{{ systemInfo.userAgent }}</span>
        </div>
      </div>

      <div class="info-group">
        <h4>帳號資訊</h4>
        <div class="info-item">
          <span class="label">使用者名稱：</span>
          <span class="value">{{ accountInfo.userName }}</span>
        </div>
        <div class="info-item">
          <span class="label">使用者 ID：</span>
          <span class="value">{{ accountInfo.userId }}</span>
          <button class="copy-btn" title="複製使用者 ID" @click="copyToClipboard(accountInfo.userId)">
            <i class="el-icon-copy-document" />
          </button>
        </div>
        <div class="info-item">
          <span class="label">使用者角色：</span>
          <span class="value">{{ accountInfo.userRole }}</span>
        </div>
        <div class="info-item">
          <span class="label">使用者 Email：</span>
          <span class="value">{{ accountInfo.userEmail }}</span>
        </div>
        <div class="info-item">
          <span class="label">組織名稱：</span>
          <span class="value">{{ accountInfo.orgName }}</span>
        </div>
        <div class="info-item">
          <span class="label">組織 ID：</span>
          <span class="value">{{ accountInfo.orgId }}</span>
          <button class="copy-btn" title="複製組織 ID" @click="copyToClipboard(accountInfo.orgId)">
            <i class="el-icon-copy-document" />
          </button>
        </div>
        <div class="info-item">
          <span class="label">組織代碼：</span>
          <span class="value">{{ accountInfo.code }}</span>
          <button class="copy-btn" title="複製組織代碼" @click="copyToClipboard(accountInfo.code)">
            <i class="el-icon-copy-document" />
          </button>
        </div>
        <div v-if="accountInfo.shopName" class="info-item">
          <span class="label">店家名稱：</span>
          <span class="value">{{ accountInfo.shopName }}</span>
        </div>
        <div v-if="accountInfo.shopId" class="info-item">
          <span class="label">店家 ID：</span>
          <span class="value">{{ accountInfo.shopId }}</span>
          <button class="copy-btn" title="複製店家 ID" @click="copyToClipboard(accountInfo.shopId)">
            <i class="el-icon-copy-document" />
          </button>
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import { defineComponent, reactive, onMounted, onUnmounted, computed } from 'vue'
import store from '@/store'
import { i18n } from '@/plugins/i18n/i18n'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'

export default defineComponent({
  name: 'SystemInfoDialog',
  components: {
    BaseDialog,
  },
  emits: ['close'],
  setup (props, { emit }) {
    const systemInfo = reactive({
      currentTime: '',
      os: '',
      browser: '',
      deviceType: '',
      screenResolution: '',
      windowSize: '',
      language: '',
      browserLanguages: '',
      displayLanguage: '',
      networkStatus: '',
      timezone: '',
      userAgent: '',
    })

    const accountInfo = computed(() => {
      const user = store.getters.user || {}
      const org = store.state.org.org || {}
      const shopId = store.getters.shop
      const shopName = store.getters.shopName

      return {
        userName: user.name || '未知',
        userId: user.id || '未知',
        userRole: store.getters.role || '未知',
        userEmail: user.email || '未知',
        orgName: org.name || '未知',
        orgId: store.getters.orgId || '未知',
        shopName: shopName !== '請選擇分店' ? shopName : '',
        shopId: shopId || '',
        code: org.code || '',
      }
    })

    // 格式化當前時間
    const formatCurrentTime = () => {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
    }

    // 更新當前時間
    const updateCurrentTime = () => {
      systemInfo.currentTime = formatCurrentTime()
    }

    // 檢測作業系統
    const detectOS = () => {
      const userAgent = navigator.userAgent
      if (userAgent.includes('Windows')) return 'Windows'
      if (userAgent.includes('Mac')) return 'macOS'
      if (userAgent.includes('Linux')) return 'Linux'
      if (userAgent.includes('Android')) return 'Android'
      if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS'
      return '未知'
    }

    // 檢測瀏覽器
    const detectBrowser = () => {
      const userAgent = navigator.userAgent
      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        const version = userAgent.match(/Chrome\/(\d+)/)?.[1]
        return `Chrome ${version || ''}`
      }
      if (userAgent.includes('Firefox')) {
        const version = userAgent.match(/Firefox\/(\d+)/)?.[1]
        return `Firefox ${version || ''}`
      }
      if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        const version = userAgent.match(/Version\/(\d+)/)?.[1]
        return `Safari ${version || ''}`
      }
      if (userAgent.includes('Edg')) {
        const version = userAgent.match(/Edg\/(\d+)/)?.[1]
        return `Edge ${version || ''}`
      }
      return '未知瀏覽器'
    }

    // 檢測裝置類型
    const detectDeviceType = () => {
      const userAgent = navigator.userAgent
      if (/Mobi|Android/i.test(userAgent)) return '行動裝置'
      if (/Tablet|iPad/i.test(userAgent)) return '平板電腦'
      return '桌面電腦'
    }

    // 檢測網路狀態
    const detectNetworkStatus = () => {
      return navigator.onLine ? '線上' : '離線'
    }

    // 取得顯示語言的中文名稱
    const getDisplayLanguageName = (locale) => {
      return locale
    }

    // 初始化系統資訊
    const initSystemInfo = () => {
      updateCurrentTime()
      systemInfo.os = detectOS()
      systemInfo.browser = detectBrowser()
      systemInfo.deviceType = detectDeviceType()
      systemInfo.screenResolution = `${screen.width} x ${screen.height}`
      systemInfo.windowSize = `${window.innerWidth} x ${window.innerHeight}`
      systemInfo.language = navigator.language || '未知'
      systemInfo.browserLanguages = navigator.languages ? navigator.languages.join(', ') : '未知'
      systemInfo.displayLanguage = getDisplayLanguageName(i18n.locale)
      systemInfo.networkStatus = detectNetworkStatus()
      systemInfo.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '未知'
      systemInfo.userAgent = navigator.userAgent
    }

    // 複製單個值到剪貼簿
    const copyToClipboard = async (text) => {
      if (typeof text === 'string') {
        // 複製單個值
        try {
          await navigator.clipboard.writeText(text)
          window.$message.success('已複製到剪貼簿')
        } catch (error) {
          console.error('複製失敗:', error)
          window.$message.error('複製失敗，請手動選取複製')
        }
      } else {
        // 複製完整系統資訊
        copyAllSystemInfo()
      }
    }

    // 複製完整系統資訊到剪貼簿
    const copyAllSystemInfo = async () => {
      const account = accountInfo.value
      const shopInfo = account.shopName ? `
店家名稱：${account.shopName}
店家 ID：${account.shopId}` : ''

      const text = `資訊擷取時間：${systemInfo.currentTime}

系統資訊
================
作業系統：${systemInfo.os}
瀏覽器：${systemInfo.browser}
裝置類型：${systemInfo.deviceType}
螢幕解析度：${systemInfo.screenResolution}
視窗大小：${systemInfo.windowSize}
使用語言：${systemInfo.language}
瀏覽器語言：${systemInfo.browserLanguages}
i18n語言：${systemInfo.displayLanguage}
網路狀態：${systemInfo.networkStatus}
時區：${systemInfo.timezone}

帳號資訊
================
使用者名稱：${account.userName}
使用者 ID：${account.userId}
使用者角色：${account.userRole}
使用者 Email：${account.userEmail}
組織名稱：${account.orgName}
組織 ID：${account.orgId}${shopInfo}

User Agent：${systemInfo.userAgent}`

      try {
        await navigator.clipboard.writeText(text)
        window.$message.success('系統資訊已複製到剪貼簿')
      } catch (error) {
        console.error('複製失敗:', error)
        window.$message.error('複製失敗，請手動選取複製')
      }
    }

    const onClose = () => {
      emit('close')
    }

    let timeInterval = null

    onMounted(() => {
      initSystemInfo()

      // 設置定時器，每秒更新時間
      timeInterval = setInterval(() => {
        updateCurrentTime()
      }, 1000)

      // 監聽網路狀態變化
      window.addEventListener('online', () => {
        systemInfo.networkStatus = '線上'
      })
      window.addEventListener('offline', () => {
        systemInfo.networkStatus = '離線'
      })

      // 監聽視窗大小變化
      window.addEventListener('resize', () => {
        systemInfo.windowSize = `${window.innerWidth} x ${window.innerHeight}`
      })
    })

    onUnmounted(() => {
      // 清理定時器
      if (timeInterval) {
        clearInterval(timeInterval)
      }
    })

    return {
      systemInfo,
      accountInfo,
      copyToClipboard,
      copyAllSystemInfo,
      onClose,
    }
  },
})
</script>

<style lang="postcss" scoped>
.system-info {
  .current-time {
    text-align: center;
    padding: 12px 16px;
    border-radius: 8px;
    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 1px;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
  }

  .info-group {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    h4 {
      font-size: 16px;
      font-weight: 600;
      color: #2d271b;
      margin: 0 0 12px 0;
      padding-bottom: 8px;
      border-bottom: 1px solid #e8e8e8;
    }

    .info-item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      line-height: 1.5;

      .label {
        flex-shrink: 0;
        width: 120px;
        font-weight: 500;
        color: #6d7281;
      }

      .value {
        flex: 1;
        color: #2d271b;
        word-break: break-all;

        &.user-agent {
          font-size: 12px;
          line-height: 1.4;
        }
      }

      .copy-btn {
        margin-left: 8px;
        padding: 4px 6px;
        background: #f5f7fa;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        color: #606266;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.3s;

        &:hover {
          background: #ecf5ff;
          border-color: #409eff;
          color: #409eff;
        }

        &:active {
          background: #3a8ee6;
          border-color: #3a8ee6;
          color: #fff;
        }

        i {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
